import { Link } from "@tanstack/react-router";
import { usePageSeo } from "@/hooks/use-page-seo";
import { pageSeo } from "@/lib/seo";
import {
  ArrowRight,
  BarChart3,
  Building2,
  CalendarDays,
  ClipboardList,
  CreditCard,
  Home,
  Users,
  Wrench,
} from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fadeUp, stagger } from "@/lib/animations";

interface CapabilityProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}

const Capability = ({ icon: Icon, title, description }: CapabilityProps) => (
  <div className="flex gap-4">
    <Icon className="mt-0.5 h-5 w-5 shrink-0 text-neutral-700" aria-hidden="true" />
    <div>
      <h3 className="font-semibold text-neutral-900">{title}</h3>
      <p className="mt-1 text-sm leading-relaxed text-neutral-600">{description}</p>
    </div>
  </div>
);

const EstmanPage = () => {
  usePageSeo(pageSeo.estman);
  return (
    <div className="min-h-screen">

      {/* ── Hero ── */}
      <section className="relative w-full overflow-hidden py-20">
        <img
          src="/allimages/softwaredevteam.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover scale-105"
          style={{ filter: "blur(4px)" }}
        />
        <div className="absolute inset-0 bg-white/80" />
        <motion.div
          className="relative z-10 mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8"
          variants={stagger}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={fadeUp}>
            <Building2 className="mx-auto mb-6 h-12 w-12 text-neutral-700" aria-hidden="true" />
            <Badge variant="outline" className="mb-4 text-xs font-medium text-neutral-500">
              Property management system
            </Badge>
          </motion.div>
          <motion.h1
            variants={fadeUp}
            className="mb-4 text-4xl font-bold tracking-tight text-neutral-900 md:text-5xl"
          >
            Estman
          </motion.h1>
          <motion.p variants={fadeUp} className="mb-8 text-xl font-medium text-neutral-500">
            Property portfolio management for African markets
          </motion.p>
          <motion.p variants={fadeUp} className="mx-auto max-w-2xl text-lg leading-relaxed text-neutral-600">
            Estman gives property managers, landlords, and agencies a single system for
            managing their full portfolio — from property listings and lease agreements to
            tenant communication, payment tracking, and maintenance workflows.
          </motion.p>
        </motion.div>
      </section>

      {/* ── Problem / Solution ── */}
      <section className="w-full py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            className="grid gap-12 lg:grid-cols-2"
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
          >
            <motion.div variants={fadeUp}>
              <h2 className="text-2xl font-bold text-neutral-900">The problem it solves</h2>
              <p className="mt-4 leading-relaxed text-neutral-600">
                Property management in Africa often involves fragmented tools: lease
                agreements tracked in spreadsheets, rent collected informally, maintenance
                requests managed by phone. As portfolios grow, this becomes unscalable —
                missed payments, overlooked maintenance, and landlords with no clear view
                of their income.
              </p>
              <p className="mt-4 leading-relaxed text-neutral-600">
                Estman centralises all of this. Every property, landlord, tenant, and payment
                is in one place, with clear records and automated workflows that keep
                everything running without manual chasing.
              </p>
            </motion.div>
            <motion.div variants={fadeUp}>
              <h2 className="text-2xl font-bold text-neutral-900">Built for the African context</h2>
              <p className="mt-4 leading-relaxed text-neutral-600">
                Estman is designed with the realities of property management in Zimbabwe and
                the broader African region in mind — including multi-currency support,
                informal payment methods, and the kinds of lease structures common in this
                market.
              </p>
              <p className="mt-4 leading-relaxed text-neutral-600">
                It is not a port of a Western property management tool. It was designed from
                the ground up for property managers who work the way property managers here
                actually work.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── Capabilities ── */}
      <section className="w-full bg-stone-50 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            className="mb-12 text-center"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-neutral-900">What Estman includes</h2>
            <p className="mt-4 text-neutral-600">
              A complete toolkit for managing residential and commercial property portfolios.
            </p>
          </motion.div>
          <motion.div
            className="grid gap-8 md:grid-cols-2 lg:grid-cols-3"
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
          >
            {[
              { icon: Home, title: "Property management", description: "Manage your full portfolio in one place — unit details, photos, documents, and availability — across multiple properties and locations." },
              { icon: Users, title: "Landlord and tenant records", description: "Centralised profiles for landlords and tenants, with full history: leases, payments, correspondence, and maintenance requests." },
              { icon: CalendarDays, title: "Lease management", description: "Create, renew, and track lease agreements. Get alerts on upcoming renewals and view the full timeline of any tenancy at a glance." },
              { icon: CreditCard, title: "Payment tracking", description: "Record and track rent payments, deposits, and outstanding balances. Generate statements for tenants and landlords on demand." },
              { icon: Wrench, title: "Maintenance workflows", description: "Tenants log maintenance requests, managers assign them, and the system tracks progress from submission to resolution." },
              { icon: Building2, title: "Agency management", description: "For property agencies managing multiple landlords: keep each client's portfolio separate while maintaining a unified view across your book." },
              { icon: ClipboardList, title: "Inspections and reports", description: "Schedule and document property inspections. Generate standardised condition reports that protect both landlords and tenants." },
              { icon: BarChart3, title: "Financial reporting", description: "Income and expense summaries per property or across the portfolio. Clear, exportable reports for landlords and management review." },
            ].map(({ icon, title, description }) => (
              <motion.div key={title} variants={fadeUp}>
                <Capability icon={icon} title={title} description={description} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="w-full bg-neutral-950 py-16">
        <motion.div
          className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.h2 variants={fadeUp} className="text-2xl font-bold text-white">
            Interested in Estman?
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-3 text-neutral-300">
            Tell us about your portfolio and we'll show you how Estman could simplify
            your operations.
          </motion.p>
          <motion.div variants={fadeUp} className="mt-6">
            <Button asChild size="lg" className="bg-primary text-white hover:bg-primary/90">
              <Link to="/contact" search={{ subject: "Estman Enquiry" } as Record<string, string>}>
                Get in touch <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
              </Link>
            </Button>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
};

export default EstmanPage;
